'use client';

import { motion, useReducedMotion } from 'framer-motion';

const EASE_OUT_QUART = [0.25, 1, 0.5, 1];

const defaultVariants = {
  fadeUp: {
    hidden: { opacity: 0, y: 24 },
    visible: { opacity: 1, y: 0 },
  },
  fadeIn: {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  },
  fadeScale: {
    hidden: { opacity: 0, scale: 0.96 },
    visible: { opacity: 1, scale: 1 },
  },
  slideLeft: {
    hidden: { opacity: 0, x: 40 },
    visible: { opacity: 1, x: 0 },
  },
  slideRight: {
    hidden: { opacity: 0, x: -40 },
    visible: { opacity: 1, x: 0 },
  },
};

export function FadeUp({
  children, delay = 0, duration = 0.7, className, as = 'div', ...props
}) {
  const shouldReduce = useReducedMotion();
  const Component = motion[as] || motion.div;

  return (
    <Component
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-60px' }}
      variants={shouldReduce ? defaultVariants.fadeIn : defaultVariants.fadeUp}
      transition={{ duration, delay, ease: EASE_OUT_QUART }}
      className={className}
      {...props}
    >
      {children}
    </Component>
  );
}

export function FadeIn({
  children, delay = 0, duration = 0.6, className, as = 'div', ...props
}) {
  const shouldReduce = useReducedMotion();
  const Component = motion[as] || motion.div;

  return (
    <Component
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-40px' }}
      variants={defaultVariants.fadeIn}
      transition={{ duration, delay, ease: EASE_OUT_QUART }}
      className={className}
      {...props}
    >
      {children}
    </Component>
  );
}

export function FadeScale({
  children, delay = 0, duration = 0.7, className, as = 'div', ...props
}) {
  const shouldReduce = useReducedMotion();
  const Component = motion[as] || motion.div;

  return (
    <Component
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-60px' }}
      variants={shouldReduce ? defaultVariants.fadeIn : defaultVariants.fadeScale}
      transition={{ duration, delay, ease: EASE_OUT_QUART }}
      className={className}
      {...props}
    >
      {children}
    </Component>
  );
}

export function Stagger({
  children, stagger = 0.08, delay = 0, className, as = 'div', ...props
}) {
  const Component = motion[as] || motion.div;

  return (
    <Component
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-60px' }}
      variants={{
        visible: {
          transition: {
            staggerChildren: stagger,
            delayChildren: delay,
          },
        },
      }}
      className={className}
      {...props}
    >
      {children}
    </Component>
  );
}

export function StaggerChild({
  children, className, as = 'div', duration = 0.6, ...props
}) {
  const shouldReduce = useReducedMotion();
  const Component = motion[as] || motion.div;

  return (
    <Component
      variants={shouldReduce ? defaultVariants.fadeIn : defaultVariants.fadeUp}
      transition={{ duration, ease: EASE_OUT_QUART }}
      className={className}
      {...props}
    >
      {children}
    </Component>
  );
}

export { motion };
